import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    Shield,
    ShieldAlert,
    ShieldCheck,
    ShieldX,
    AlertTriangle,
    Ban,
    RefreshCw,
    Eye,
    Lock,
    Unlock,
    Server,
    User,
    Globe,
    Activity,
    Zap,
    Target,
    Clock,
    CheckCircle,
    XCircle,
    Scan,
    AlertOctagon
} from 'lucide-react';
import { FeatureGate } from '../components/common/FeatureGate';
import { toast } from 'sonner';
import {
    getThreats,
    getThreatStats,
    getBlockedList,
    scanAllThreats,
    blockThreat,
    resolveThreat,
    blockTarget,
    unblockTarget
} from '../lib/api';

const ThreatDetectionContent = () => {
    const [threats, setThreats] = useState([]);
    const [stats, setStats] = useState(null);
    const [blockedList, setBlockedList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [selectedThreat, setSelectedThreat] = useState(null);
    const [showBlockDialog, setShowBlockDialog] = useState(false);
    const [blockForm, setBlockForm] = useState({ target_type: 'ip', target_value: '', reason: '', duration_hours: 24 });
    const [activeTab, setActiveTab] = useState('threats');

    const fetchThreatsAsync = async () => {
        try {
            const response = await getThreats();
            const data = response.data;
            setThreats(Array.isArray(data) ? data : data?.threats || []);
        } catch (error) {
            toast.error('Failed to fetch threats');
        }
    };

    const fetchStatsAsync = async () => {
        try {
            const response = await getThreatStats();
            setStats(response.data);
        } catch (error) {
            toast.error('Failed to fetch threat stats');
        }
    };

    const fetchBlockedListAsync = async () => {
        try {
            const response = await getBlockedList();
            setBlockedList(response.data);
        } catch (error) {
            toast.error('Failed to fetch blocked list');
        }
    };

    const fetchAllData = async () => {
        setLoading(true);
        await Promise.all([fetchThreatsAsync(), fetchStatsAsync(), fetchBlockedListAsync()]);
        setLoading(false);
    };

    const handleScanAll = async () => {
        setScanning(true);
        try {
            await scanAllThreats();
            await fetchAllData();
        } catch (error) {
            toast.error('Failed to scan threats');
        }
        setScanning(false);
    };

    const handleBlockThreat = async (threatId) => {
        try {
            await blockThreat(threatId);
            await fetchAllData();
        } catch (error) {
            toast.error('Failed to block threat');
        }
    };

    const handleResolveThreat = async (threatId) => {
        try {
            await resolveThreat(threatId);
            await fetchAllData();
        } catch (error) {
            toast.error('Failed to resolve threat');
        }
    };

    const handleBlockTarget = async () => {
        try {
            await blockTarget(blockForm);
            setShowBlockDialog(false);
            setBlockForm({ target_type: 'ip', target_value: '', reason: '', duration_hours: 24 });
            await fetchAllData();
        } catch (error) {
            toast.error('Failed to block target');
        }
    };

    const handleUnblock = async (targetType, targetValue) => {
        try {
            await unblockTarget(targetType, targetValue);
            await fetchAllData();
        } catch (error) {
            toast.error('Failed to unblock target');
        }
    };

    useEffect(() => {
        fetchAllData();
        const interval = setInterval(() => fetchAllData(), 20000);
        return () => clearInterval(interval);
    }, []);

    const getSeverityBadge = (severity) => {
        const styles = {
            critical: 'bg-red-500 text-white',
            high: 'bg-orange-500 text-white',
            medium: 'bg-yellow-500 text-black',
            low: 'bg-green-500 text-white'
        };
        return <Badge className={styles[severity] || 'bg-gray-500 text-white'}>{severity?.toUpperCase()}</Badge>;
    };

    const getStatusBadge = (status) => {
        const styles = {
            active: 'bg-red-100 text-red-800 border-red-300',
            blocked: 'bg-orange-100 text-orange-800 border-orange-300',
            resolved: 'bg-green-100 text-green-800 border-green-300'
        };
        return <Badge variant="outline" className={styles[status]}>{status}</Badge>;
    };

    const getThreatIcon = (threatType) => {
        const icons = {
            brute_force: <Lock className="h-5 w-5 text-red-500" />,
            credential_stuffing: <AlertOctagon className="h-5 w-5 text-red-500" />,
            suspicious_access: <Eye className="h-5 w-5 text-yellow-500" />,
            data_exfiltration: <Server className="h-5 w-5 text-orange-500" />,
            malware_indicator: <ShieldX className="h-5 w-5 text-red-500" />,
            privilege_escalation: <Zap className="h-5 w-5 text-orange-500" />,
            insider_threat: <User className="h-5 w-5 text-yellow-500" />,
            phishing_attempt: <Globe className="h-5 w-5 text-red-500" />
        };
        return icons[threatType] || <AlertTriangle className="h-5 w-5 text-gray-500" />;
    };

    if (loading) {
        return (
            <div className="p-6">
                <Card>
                    <CardContent className="flex items-center justify-center py-12">
                        <ShieldAlert className="h-8 w-8 animate-pulse text-primary mr-3" />
                        <span>Loading threat detection...</span>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg">
                        <ShieldAlert className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Threat Detection & Prevention</h1>
                        <p className="text-sm text-muted-foreground">AI-powered security monitoring</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={fetchAllData} data-testid="refresh-threats-btn">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                    <Button onClick={handleScanAll} disabled={scanning} data-testid="scan-all-btn">
                        <Scan className={`h-4 w-4 mr-2 ${scanning ? 'animate-spin' : ''}`} />
                        {scanning ? 'Scanning...' : 'Scan All Activity'}
                    </Button>
                    <Button variant="destructive" onClick={() => setShowBlockDialog(true)} data-testid="manual-block-btn">
                        <Ban className="h-4 w-4 mr-2" />
                        Manual Block
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <Card className="border-l-4 border-l-red-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Active Threats</p>
                                    <p className="text-3xl font-bold text-red-500">{stats.by_status?.active || 0}</p>
                                </div>
                                <ShieldX className="h-10 w-10 text-red-500 opacity-20" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-orange-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Critical Severity</p>
                                    <p className="text-3xl font-bold text-orange-500">{stats.by_severity?.critical || 0}</p>
                                </div>
                                <AlertOctagon className="h-10 w-10 text-orange-500 opacity-20" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-yellow-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Last 24 Hours</p>
                                    <p className="text-3xl font-bold">{stats.last_24h || 0}</p>
                                </div>
                                <Clock className="h-10 w-10 text-yellow-500 opacity-20" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-blue-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Blocked</p>
                                    <p className="text-3xl font-bold">{stats.by_status?.blocked || 0}</p>
                                </div>
                                <Ban className="h-10 w-10 text-blue-500 opacity-20" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-green-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Resolved</p>
                                    <p className="text-3xl font-bold text-green-500">{stats.by_status?.resolved || 0}</p>
                                </div>
                                <ShieldCheck className="h-10 w-10 text-green-500 opacity-20" />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="threats" data-testid="tab-threats">
                        <ShieldAlert className="h-4 w-4 mr-2" />
                        Detected Threats ({threats.length})
                    </TabsTrigger>
                    <TabsTrigger value="blocked" data-testid="tab-blocked">
                        <Ban className="h-4 w-4 mr-2" />
                        Blocked ({blockedList?.total_blocked || 0})
                    </TabsTrigger>
                    <TabsTrigger value="analysis" data-testid="tab-analysis">
                        <Activity className="h-4 w-4 mr-2" />
                        Threat Analysis
                    </TabsTrigger>
                </TabsList>

                {/* Threats Tab */}
                <TabsContent value="threats" className="space-y-4">
                    {threats.length > 0 ? (
                        <div className="space-y-4">
                            {threats.map((threat) => (
                                <Card key={threat.id} className={`border-l-4 ${
                                    threat.severity === 'critical' ? 'border-l-red-500' :
                                    threat.severity === 'high' ? 'border-l-orange-500' :
                                    threat.severity === 'medium' ? 'border-l-yellow-500' :
                                    'border-l-green-500'
                                }`}>
                                    <CardContent className="pt-6">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-4">
                                                <div className="p-2 bg-muted rounded-lg">
                                                    {getThreatIcon(threat.threat_type)}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h3 className="font-bold">{threat.threat_type?.replace(/_/g, ' ').toUpperCase()}</h3>
                                                        {getSeverityBadge(threat.severity)}
                                                        {getStatusBadge(threat.status)}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground">{threat.summary}</p>
                                                    
                                                    {/* Targeted Assets */}
                                                    {threat.analysis?.targeted_assets && (
                                                        <div className="mt-3 p-2 bg-muted rounded text-sm">
                                                            {threat.analysis.targeted_assets.accounts?.length > 0 && (
                                                                <p><strong>Accounts:</strong> {threat.analysis.targeted_assets.accounts.join(', ')}</p>
                                                            )}
                                                            {threat.analysis.targeted_assets.devices?.length > 0 && (
                                                                <p><strong>Devices:</strong> {threat.analysis.targeted_assets.devices.join(', ')}</p>
                                                            )}
                                                        </div>
                                                    )}
                                                    
                                                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                                                        <span>Risk Score: {threat.risk_score || threat.analysis?.risk_score || 'N/A'}</span>
                                                        <span>Confidence: {threat.confidence_score || threat.analysis?.confidence_score}%</span>
                                                        <span>{new Date(threat.created_at).toLocaleString()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="flex gap-2">
                                                <Button 
                                                    size="sm" 
                                                    variant="outline"
                                                    onClick={() => setSelectedThreat(threat)}
                                                    data-testid={`view-threat-${threat.id}`}
                                                >
                                                    <Eye className="h-4 w-4 mr-1" />
                                                    Details
                                                </Button>
                                                {threat.status === 'active' && (
                                                    <>
                                                        <Button 
                                                            size="sm" 
                                                            variant="destructive"
                                                            onClick={() => handleBlockThreat(threat.id)}
                                                            data-testid={`block-threat-${threat.id}`}
                                                        >
                                                            <Ban className="h-4 w-4 mr-1" />
                                                            Block
                                                        </Button>
                                                        <Button 
                                                            size="sm" 
                                                            variant="outline"
                                                            onClick={() => handleResolveThreat(threat.id)}
                                                            data-testid={`resolve-threat-${threat.id}`}
                                                        >
                                                            <CheckCircle className="h-4 w-4 mr-1" />
                                                            Resolve
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <Card>
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <ShieldCheck className="h-16 w-16 text-green-500 mb-4" />
                                <h3 className="text-xl font-bold">No Active Threats</h3>
                                <p className="text-muted-foreground">Your systems are currently secure</p>
                                <Button className="mt-4" onClick={handleScanAll}>
                                    <Scan className="h-4 w-4 mr-2" />
                                    Run Security Scan
                                </Button>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                {/* Blocked Tab */}
                <TabsContent value="blocked" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Blocked IPs */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Globe className="h-5 w-5" />
                                    Blocked IPs ({blockedList?.blocked_ips?.length || 0})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {blockedList?.blocked_ips?.length > 0 ? (
                                    <div className="space-y-2">
                                        {blockedList.blocked_ips.map((item, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded">
                                                <div>
                                                    <p className="font-mono text-sm">{item.ip}</p>
                                                    <p className="text-xs text-muted-foreground">{item.reason}</p>
                                                </div>
                                                <Button size="sm" variant="ghost" onClick={() => handleUnblock('ip', item.ip)}>
                                                    <Unlock className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-center py-4">No blocked IPs</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Disabled Accounts */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <User className="h-5 w-5" />
                                    Disabled Accounts ({blockedList?.disabled_accounts?.length || 0})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {blockedList?.disabled_accounts?.length > 0 ? (
                                    <div className="space-y-2">
                                        {blockedList.disabled_accounts.map((item, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded">
                                                <div>
                                                    <p className="font-medium text-sm">{item.name || item.email}</p>
                                                    <p className="text-xs text-muted-foreground">{item.disabled_reason}</p>
                                                </div>
                                                <Button size="sm" variant="ghost" onClick={() => handleUnblock('account', item.email)}>
                                                    <Unlock className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-center py-4">No disabled accounts</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Isolated Devices */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Server className="h-5 w-5" />
                                    Isolated Devices ({blockedList?.isolated_devices?.length || 0})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {blockedList?.isolated_devices?.length > 0 ? (
                                    <div className="space-y-2">
                                        {blockedList.isolated_devices.map((item, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded">
                                                <div>
                                                    <p className="font-medium text-sm">{item.name}</p>
                                                    <p className="text-xs text-muted-foreground">{item.isolation_reason}</p>
                                                </div>
                                                <Button size="sm" variant="ghost" onClick={() => handleUnblock('device', item.name)}>
                                                    <Unlock className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-center py-4">No isolated devices</p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Analysis Tab */}
                <TabsContent value="analysis" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Threat Types Distribution */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Threat Types Distribution</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {stats?.by_type && Object.keys(stats.by_type).length > 0 ? (
                                    <div className="space-y-3">
                                        {Object.entries(stats.by_type).map(([type, count]) => (
                                            <div key={type} className="flex items-center gap-3">
                                                {getThreatIcon(type)}
                                                <div className="flex-1">
                                                    <div className="flex justify-between text-sm mb-1">
                                                        <span className="capitalize">{type.replace(/_/g, ' ')}</span>
                                                        <span>{count}</span>
                                                    </div>
                                                    <Progress value={(count / stats.total_threats) * 100} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-center py-4">No threat data available</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Severity Distribution */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Severity Distribution</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {stats?.by_severity ? (
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full bg-red-500" />
                                            <div className="flex-1">
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span>Critical</span>
                                                    <span>{stats.by_severity.critical || 0}</span>
                                                </div>
                                                <Progress value={((stats.by_severity.critical || 0) / Math.max(stats.total_threats, 1)) * 100} className="bg-red-100" />
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full bg-orange-500" />
                                            <div className="flex-1">
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span>High</span>
                                                    <span>{stats.by_severity.high || 0}</span>
                                                </div>
                                                <Progress value={((stats.by_severity.high || 0) / Math.max(stats.total_threats, 1)) * 100} className="bg-orange-100" />
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full bg-yellow-500" />
                                            <div className="flex-1">
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span>Medium</span>
                                                    <span>{stats.by_severity.medium || 0}</span>
                                                </div>
                                                <Progress value={((stats.by_severity.medium || 0) / Math.max(stats.total_threats, 1)) * 100} className="bg-yellow-100" />
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full bg-green-500" />
                                            <div className="flex-1">
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span>Low</span>
                                                    <span>{stats.by_severity.low || 0}</span>
                                                </div>
                                                <Progress value={((stats.by_severity.low || 0) / Math.max(stats.total_threats, 1)) * 100} className="bg-green-100" />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-center py-4">No severity data available</p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Threat Details Dialog */}
            <Dialog open={!!selectedThreat} onOpenChange={() => setSelectedThreat(null)}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {selectedThreat && getThreatIcon(selectedThreat.threat_type)}
                            Threat Details
                        </DialogTitle>
                    </DialogHeader>
                    {selectedThreat && (
                        <div className="space-y-4">
                            <div className="flex gap-2">
                                {getSeverityBadge(selectedThreat.severity)}
                                {getStatusBadge(selectedThreat.status)}
                            </div>
                            
                            <div>
                                <h4 className="font-medium mb-1">Summary</h4>
                                <p className="text-sm text-muted-foreground">{selectedThreat.summary}</p>
                            </div>
                            
                            {selectedThreat.analysis?.detailed_analysis && (
                                <div>
                                    <h4 className="font-medium mb-1">Detailed Analysis</h4>
                                    <p className="text-sm text-muted-foreground">{selectedThreat.analysis.detailed_analysis}</p>
                                </div>
                            )}
                            
                            {selectedThreat.analysis?.indicators_of_compromise?.length > 0 && (
                                <div>
                                    <h4 className="font-medium mb-1">Indicators of Compromise</h4>
                                    <div className="space-y-1">
                                        {selectedThreat.analysis.indicators_of_compromise.map((ioc, idx) => (
                                            <div key={idx} className="text-sm p-2 bg-muted rounded">
                                                <span className="font-mono">{ioc.value}</span>
                                                <span className="text-muted-foreground ml-2">({ioc.type})</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {selectedThreat.analysis?.prevention_actions && (
                                <div>
                                    <h4 className="font-medium mb-1">Prevention Actions</h4>
                                    {selectedThreat.analysis.prevention_actions.immediate?.length > 0 && (
                                        <div className="mb-2">
                                            <p className="text-sm font-medium text-red-500">Immediate:</p>
                                            <ul className="text-sm list-disc list-inside">
                                                {selectedThreat.analysis.prevention_actions.immediate.map((action, idx) => (
                                                    <li key={idx}>{action}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Manual Block Dialog */}
            <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Block Target Manually</DialogTitle>
                        <DialogDescription>Block an IP address, account, or device</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Target Type</Label>
                            <select 
                                className="w-full p-2 border rounded mt-1"
                                value={blockForm.target_type}
                                onChange={(e) => setBlockForm({...blockForm, target_type: e.target.value})}
                            >
                                <option value="ip">IP Address</option>
                                <option value="account">Account (Email)</option>
                                <option value="device">Device</option>
                            </select>
                        </div>
                        <div>
                            <Label>Target Value</Label>
                            <Input 
                                placeholder={blockForm.target_type === 'ip' ? '192.168.1.1' : blockForm.target_type === 'account' ? 'user@example.com' : 'Device Name'}
                                value={blockForm.target_value}
                                onChange={(e) => setBlockForm({...blockForm, target_value: e.target.value})}
                            />
                        </div>
                        <div>
                            <Label>Reason</Label>
                            <Input 
                                placeholder="Reason for blocking"
                                value={blockForm.reason}
                                onChange={(e) => setBlockForm({...blockForm, reason: e.target.value})}
                            />
                        </div>
                        <div>
                            <Label>Duration (hours)</Label>
                            <Input 
                                type="number"
                                value={blockForm.duration_hours}
                                onChange={(e) => setBlockForm({...blockForm, duration_hours: parseInt(e.target.value)})}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBlockDialog(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleBlockTarget}>Block</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Wrap with FeatureGate for AI tier requirement
const ThreatDetectionPage = () => {
    return (
        <FeatureGate feature="ai" featureName="AI Threat Detection">
            <ThreatDetectionContent />
        </FeatureGate>
    );
};

export default ThreatDetectionPage;
